import { fetchApiJson, validateApiPayload, waitForApiRequest } from './apiRequest.js';
// Shared response reuse. Consumer cancellation never cancels another page's request.
const responses = new Map();
const pending = new Map();
const STORAGE_KEY = 'volleywire.publicResponses.v1';
try {
  const saved = JSON.parse(globalThis.sessionStorage?.getItem(STORAGE_KEY) || '[]');
  for (const [key, entry] of Array.isArray(saved) ? saved : []) {
    if (Date.now() - entry.at < 300000 && typeof entry.body === 'string' && validateApiPayload(key, JSON.parse(entry.body))) responses.set(key, entry);
  }
} catch { /* Storage is optional, including in private browsing. */ }
function remember(key, entry) {
  responses.delete(key);
  responses.set(key, entry);
  while (responses.size > 20) responses.delete(responses.keys().next().value);
  try {
    const recent = [...responses].filter(([, value]) => Date.now() - value.at < 300000).slice(-8);
    let serialized = JSON.stringify(recent);
    while (serialized.length > 2000000 && recent.length) {
      recent.shift();
      serialized = JSON.stringify(recent);
    }
    globalThis.sessionStorage?.setItem(STORAGE_KEY, serialized);
  } catch { /* Keep the in-memory response if storage is unavailable. */ }
}
function asResponse(entry) { return new Response(entry.body, { status: entry.status, headers: entry.headers }); }
export async function cachedFetch(url, options = {}) {
  const { signal, force = false, maxAge = 60000, staleAge = 300000, ...init } = options;
  const key = String(url);
  if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
  const cached = responses.get(key);
  const age = cached ? Date.now() - cached.at : Infinity;
  const refresh = () => {
    if (!pending.has(key)) {
      const task = fetchApiJson(url, { ...init, timeoutMs: 25000 }).then(({ response, payload }) => {
        const entry = { at: Date.now(), body: JSON.stringify(payload), status: response.status, headers: [...response.headers] };
        if (response.ok && !/\bno-store\b/i.test(response.headers.get('cache-control') || '')) remember(key, entry);
        else responses.delete(key);
        return entry;
      }).finally(() => pending.delete(key));
      pending.set(key, task);
    }
    return pending.get(key);
  };
  if (!force && age < maxAge) return asResponse(cached);
  if (!force && age < staleAge) {
    refresh().catch(() => {});
    return asResponse(cached);
  }
  const entry = await waitForApiRequest(refresh(), signal);
  if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
  return asResponse(entry);
}
